import { useCallback, useEffect, useRef, useState } from 'react';
import { getCommandCenter } from '@/services/commandCenter.service.js';
import { evictStale } from '@/lib/inFlight.js';
import useIdleAware, { IDLE_POLL_MS } from '@/hooks/useIdleAware.js';

// 60s background polling while foregrounded. Pauses on visibilitychange;
// drops to 10-min cadence when visible but idle >15 min.
// Mutations should call refresh() directly for action-driven updates.
const POLL_MS = 60_000;

export default function useCommandCenter() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const aborted = useRef(false);
  const timerRef = useRef(null);
  const lastFetchRef = useRef(0);

  // load({ poll: true }) only from the timer's tick (X-Tracon-Poll, lib/poll.js); refresh() is a person's read.
  const load = useCallback(async ({ poll = false } = {}) => {
    lastFetchRef.current = Date.now();
    try {
      const d = await getCommandCenter({ poll });
      if (!aborted.current) {
        setData(d);
        setError(null);
      }
    } catch (err) {
      if (!aborted.current) setError(err);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, []);
  const refresh = useCallback(() => load(), [load]);

  useEffect(() => {
    aborted.current = false;
    refresh();
    return () => {
      aborted.current = true;
    };
  }, [refresh]);

  const isIdle = useIdleAware({
    onActive: () => {
      if (Date.now() - lastFetchRef.current > POLL_MS) refresh();
    },
  });

  useEffect(() => {
    function tick() {
      if (document.visibilityState !== 'visible') return;
      if (isIdle() && Date.now() - lastFetchRef.current < IDLE_POLL_MS) return;
      load({ poll: true });
    }
    function start() {
      if (timerRef.current) return;
      timerRef.current = setInterval(tick, POLL_MS);
    }
    function stop() {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') {
        // Wake from sleep: drop dedup slots whose promises died with the
        // socket, so this refresh fetches instead of awaiting a corpse.
        evictStale();
        refresh();
        start();
      } else {
        stop();
      }
    }
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, load, isIdle]);

  return { data, error, loading, refresh };
}
